/* ════ IMPORT TRAVEL FROM CALENDAR CSV → CO₂ board ═════════ */
// Reads an Outlook calendar CSV export, auto-detects travel events
// (flight / hotel / train / car), guesses zone, estimates CO₂, and — after a
// review step — lands them as trips on the CO₂ board.
import { state } from './state.js';
import { $ } from './dom.js';
import { TRAVEL_MODES, TRAVEL_ZONES, estimateCo2, flightCo2, HAUL_KM, ZONE_HAUL } from './config.js';
import { ddiff, addD, pdate, fdate } from './utils/date.js';
import { sb } from './supabase/client.js';
import { setSyncDot, normTrip } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { renderCo2 } from './co2board.js';
import { setView } from './views.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
let candidates = [];

/* ── A small, correct CSV parser (handles quoted multi-line fields) ── */
function parseCSV(text) {
  const rows = []; let row = [], field = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\r') { /* skip */ }
    else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// Outlook writes dates as D/M/YYYY.
function isoDate(s) {
  const m = String(s || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : null;
}

const NOISE = /birthday|salary reminder|^reserved$|org help|servicenow|placeholder|1:1|reminder$/i;
function classify(text) {
  const t = text.toLowerCase();
  if (/pick up car|car from big|bring car in|big service|car service/.test(t)) return null; // servicing, not travel
  if (/ryanair|easyjet|\b(ba|ei|fr|lh|kl|sk|u2|af)\s?\d{2,4}\b|\bairport\b|\bflight\b|\b[a-z]{3}-[a-z]{3}\b|man-|dub-|-jfk|-mad/.test(t)) return 'flight';
  if (/hotel|marriott|hilton|premier inn|travelodge|gonville|normandy farm|confirmation number|stay dates|check ?out|\bnights?\b|park hotel|old bank/.test(t)) return 'hotel';
  if (/\btrain\b|\bstation\b|\brail\b|euston|kings ?cross|eurostar|\(cbg\)|\(spt\)/.test(t)) return 'train';
  if (/\bdrive\b|\bdriving\b|\bcar\b|taxi|uber/.test(t)) return 'car';
  if (/^travel\b|travel to|travel home/.test(t)) return 'other';
  return null;
}
function guessZone(text) {
  const t = text.toLowerCase();
  if (/jfk|phl|\bus\b|usa|canada|toronto|\bdc\b|states|new york|potomac|washington|maryland|\byyz\b/.test(t)) return 'US';
  if (/mad|madrid|zurich|zug|copenhag|oslo|stockholm|dublin|\bdub\b|switzerland|barcelona|barca|\bewr\b/.test(t)) return 'EU';
  return 'UK';
}

function parseFile(text) {
  const rows = parseCSV(text);
  if (!rows.length) throw new Error('empty file');
  const H = rows[0].map(h => h.trim().toLowerCase());
  const ix = name => H.indexOf(name);
  const cSub = ix('subject'), cSd = ix('start date'), cEd = ix('end date'), cAll = ix('all day event'),
    cLoc = ix('location'), cDesc = ix('description');
  candidates = [];
  for (const r of rows.slice(1)) {
    const subject = (r[cSub] || '').trim();
    if (!subject || NOISE.test(subject)) continue;
    const blob = `${subject} ${r[cLoc] || ''} ${r[cDesc] || ''}`;
    const mode = classify(blob);
    if (!mode) continue;
    let start = isoDate(r[cSd]); if (!start) continue;
    let end = isoDate(r[cEd]) || start;
    // Outlook all-day end dates are exclusive → pull back one day to make inclusive.
    if ((r[cAll] || '').toLowerCase() === 'true' && end > start) end = fdate(addD(pdate(end), -1));
    if (end < start) end = start;
    const zone = guessZone(blob);
    const nights = Math.max(1, ddiff(pdate(start), pdate(end)));
    // Flights use the official business-class factor × a per-haul distance;
    // each calendar leg is treated one-way.
    const distance = mode === 'flight' ? HAUL_KM[ZONE_HAUL[zone]] : null;
    const co2 = mode === 'flight' ? flightCo2(zone, distance, false) : estimateCo2(mode, zone, nights, false);
    // Stable key so a weekly re-import can skip events already brought in,
    // or ones previously deleted (don't resurrect them).
    const key = `${subject}|${start}|${end}`;
    const seen = state.trips.some(t => t.source_key === key);
    const ignored = state.tripIgnores.has(key);
    candidates.push({ subject, start, end, mode, zone, co2, distance, key, seen, ignored });
  }
  candidates.sort((a, b) => a.start.localeCompare(b.start));
}

/* ── Review modal ────────────────────────────────────────── */
function modeSel(v) { return `<select class="timp-mode">${TRAVEL_MODES.map(m => `<option value="${m.v}"${m.v === v ? ' selected' : ''}>${m.ic} ${m.l}</option>`).join('')}</select>`; }
function zoneSel(v) { return `<select class="timp-zone">${TRAVEL_ZONES.map(z => `<option${z === v ? ' selected' : ''}>${z}</option>`).join('')}</select>`; }

// A row is ticked by default only if it's a new flight (not already in / deleted).
const defaultTick = c => !c.seen && !c.ignored && c.mode === 'flight';

function openModal() {
  const flightsN = candidates.filter(defaultTick).length;
  const seenN = candidates.filter(c => c.seen).length;
  const ignoredN = candidates.filter(c => c.ignored && !c.seen).length;
  const otherN = candidates.length - flightsN - seenN - ignoredN;
  const flightTotal = candidates.filter(defaultTick).reduce((s, c) => s + c.co2, 0);
  $('timp-count').textContent = `${flightsN} new flight${flightsN === 1 ? '' : 's'} (~${Math.round(flightTotal * 1000) / 1000} t, business class)`
    + (otherN ? ` · ${otherN} other travel (unticked)` : '')
    + (seenN ? ` · ${seenN} already imported` : '')
    + (ignoredN ? ` · ${ignoredN} previously removed` : '');
  $('timp-res').innerHTML = state.resources.filter(r => r.id !== 'unassigned')
    .map(r => `<option value="${r.id}">${esc(r.name)}</option>`).join('');
  $('timp-all').checked = candidates.length > 0 && candidates.every(defaultTick);
  $('timp-preview').innerHTML = candidates.length ? candidates.map((c, i) => {
    const badge = c.seen ? '<span class="timp-in">already in</span>' : c.ignored ? '<span class="timp-in">removed</span>' : '';
    return `<div class="timp-row${c.seen || c.ignored ? ' seen' : ''}" data-i="${i}">
      <input type="checkbox" class="timp-chk"${defaultTick(c) ? ' checked' : ''}>
      <span class="timp-subj" title="${esc(c.subject)}">${esc(c.subject.slice(0, 70))}${badge}</span>
      <span class="timp-dates">${c.start}${c.end !== c.start ? ' → ' + c.end : ''}</span>
      ${modeSel(c.mode)}${zoneSel(c.zone)}
      <input class="timp-co2" type="number" min="0" step="0.001" value="${c.co2}">
    </div>`;
  }).join('') : '<div class="list-empty">No travel found in this file.</div>';
  $('timp-modal').classList.add('open');
}

// Recompute a row's CO₂ when its mode/zone changes (unless hand-edited).
function onPreviewChange(e) {
  const row = e.target.closest('.timp-row'); if (!row) return;
  if (e.target.classList.contains('timp-co2')) { row.dataset.touched = '1'; return; }
  if (e.target.classList.contains('timp-mode') || e.target.classList.contains('timp-zone')) {
    if (row.dataset.touched) return;
    const i = +row.dataset.i, c = candidates[i];
    const mode = row.querySelector('.timp-mode').value, zone = row.querySelector('.timp-zone').value;
    const nights = Math.max(1, ddiff(pdate(c.start), pdate(c.end)));
    row.querySelector('.timp-co2').value = mode === 'flight'
      ? flightCo2(zone, HAUL_KM[ZONE_HAUL[zone]], false)
      : estimateCo2(mode, zone, nights, false);
  }
}

async function doImport() {
  const resId = $('timp-res').value, me = state.currentUser?.email || null;
  const rows = [];
  $('timp-preview').querySelectorAll('.timp-row').forEach(row => {
    if (!row.querySelector('.timp-chk').checked) return;
    const c = candidates[+row.dataset.i];
    const mode = row.querySelector('.timp-mode').value, zone = row.querySelector('.timp-zone').value;
    rows.push({
      name: c.subject.slice(0, 120), resource: resId, start: c.start, end: c.end,
      mode, zone, co2: parseFloat(row.querySelector('.timp-co2').value) || 0,
      distance_km: mode === 'flight' ? HAUL_KM[ZONE_HAUL[zone]] : null,
      source_key: c.key, created_by: me,
    });
  });
  if (!rows.length) { showToast('Tick at least one trip'); return; }
  setSyncDot('busy');
  let err = null, inserted = [];
  for (let i = 0; i < rows.length && !err; i += 100) {
    const { data, error } = await sb.from('trips').insert(rows.slice(i, i + 100)).select();
    err = error; if (data) inserted.push(...data);
  }
  if (err) { setSyncDot('err'); showToast('Import failed: ' + err.message); return; }
  inserted.forEach(t => { if (!state.trips.find(x => x.id === t.id)) state.trips.push(normTrip(t)); });
  // Re-importing a previously-removed event means it's wanted again — un-ignore it.
  const revived = rows.map(r => r.source_key).filter(k => state.tripIgnores.has(k));
  if (revived.length) { revived.forEach(k => state.tripIgnores.delete(k)); sb.from('trip_ignores').delete().in('source_key', revived).then(() => {}); }
  setSyncDot('ok'); $('timp-modal').classList.remove('open');
  showToast(`Imported ${rows.length} trip${rows.length === 1 ? '' : 's'} ✓`);
  setView('co2'); renderCo2();
}

export function initTravelImport() {
  const inp = $('timp-file'); if (!inp) return;
  $('timp-btn').addEventListener('click', () => inp.click());
  inp.addEventListener('change', async e => {
    const file = e.target.files[0]; if (!file) return;
    try { parseFile(await file.text()); openModal(); }
    catch (err) { showToast('Could not read file: ' + err.message); }
    inp.value = '';
  });
  $('timp-x').addEventListener('click', () => $('timp-modal').classList.remove('open'));
  $('timp-cancel').addEventListener('click', () => $('timp-modal').classList.remove('open'));
  $('timp-run').addEventListener('click', doImport);
  $('timp-all').addEventListener('change', e => $('timp-preview').querySelectorAll('.timp-chk').forEach(c => { c.checked = e.target.checked; }));
  $('timp-preview').addEventListener('change', onPreviewChange);
  $('timp-modal').addEventListener('click', e => { if (e.target.id === 'timp-modal') $('timp-modal').classList.remove('open'); });
}
